import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { RefreshCw, CircleCheck, CircleAlert, ExternalLink } from 'lucide-react';
import { serverHealthService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import SegmentedControl from '../components/SegmentedControl';
import StackedTable from '../components/StackedTable';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { EMPTY, formatDateTime, formatNumber, formatTime, plural } from '../utils/format';

// Elastic Beanstalk refreshes enhanced health every 10 seconds; every 30 is plenty for a person watching
const REFRESH_INTERVAL_MS = 30 * 1000;

const EVENT_WINDOWS = [
  { value: 24, label: '24h', period: '24 hours' },
  { value: 72, label: '3 days', period: '3 days' },
  { value: 168, label: '7 days', period: '7 days' },
];

// Enhanced health statuses, worst last. Info and Pending mean an operation (usually a deployment) is running.
const STATUS_VARIANT = {
  Ok: 'success',
  Info: 'primary',
  Pending: 'primary',
  Warning: 'warning',
  Degraded: 'danger',
  Severe: 'danger',
  NoData: 'secondary',
  Unknown: 'secondary',
  Suspended: 'secondary',
};
const STATUS_LABEL = { Ok: 'OK', NoData: 'No data' };
const SETTLED = ['Ok', 'Info', 'Pending'];

const HealthBadge = ({ status }) => (
  <Badge bg={STATUS_VARIANT[status] || 'secondary'}>{STATUS_LABEL[status] || status || 'Unknown'}</Badge>
);

const needsAttention = (env) => Boolean(env.error) || !SETTLED.includes(env.healthStatus);

/** EB reports latency in seconds. */
const formatLatency = (seconds) => {
  if (seconds == null) return EMPTY;
  return seconds < 1 ? `${Math.round(seconds * 1000)} ms` : `${seconds.toFixed(1)} s`;
};

/** "12 min ago", "3 h ago", "4 days ago". */
const ago = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return EMPTY;
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const consoleUrl = (region, environmentId) =>
  `https://${region}.console.aws.amazon.com/elasticbeanstalk/home?region=${region}#/environment/dashboard?environmentId=${environmentId}`;

const scrollToEnvironment = (name) =>
  document.getElementById(`env-${name}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const RequestSummary = ({ env }) => {
  const r = env.requests;
  if (!r) {
    return (
      <span className="text-muted">
        {env.tier === 'Worker' ? 'Worker environment, no web requests.' : 'No request data in the last 10 seconds.'}
      </span>
    );
  }
  return (
    <span className="text-muted">
      {plural(r.count, 'request')} in the last {r.windowSeconds || 10} s
      {' · '}
      <span className={r.status5xx > 0 ? 'text-danger fw-semibold' : undefined}>{formatNumber(r.status5xx ?? 0)} with 5xx</span>
      {' · '}p99 {formatLatency(r.p99)}
    </span>
  );
};

const InstanceRow = ({ instance }) => (
  <tr>
    <td className="align-middle">
      <span className="font-monospace">{instance.id}</span>
      <div className="small text-muted">
        {[instance.availabilityZone, instance.instanceType].filter(Boolean).join(' · ') || EMPTY}
      </div>
    </td>
    <td className="align-middle"><HealthBadge status={instance.healthStatus} /></td>
    <td className="align-middle stack-block small">
      {instance.causes.length ? instance.causes.join(' ') : <span className="text-muted">{EMPTY}</span>}
    </td>
    <td className="align-middle text-end">{instance.requests ? formatNumber(instance.requests.count) : EMPTY}</td>
    <td className={`align-middle text-end${instance.requests?.status5xx > 0 ? ' text-danger fw-semibold' : ''}`}>
      {instance.requests ? formatNumber(instance.requests.status5xx ?? 0) : EMPTY}
    </td>
    <td className="align-middle text-end text-nowrap">{formatLatency(instance.requests?.p99)}</td>
    <td className="align-middle text-end">{instance.cpuBusy == null ? EMPTY : `${instance.cpuBusy}%`}</td>
    <td className="align-middle text-end d-none d-lg-table-cell">
      {instance.loadAverage == null ? EMPTY : instance.loadAverage.toFixed(2)}
    </td>
    <td className="align-middle text-nowrap" title={formatDateTime(instance.launchedAt)}>{ago(instance.launchedAt)}</td>
    <td className="align-middle d-none d-xl-table-cell small">
      <span className="font-monospace">{instance.versionLabel || EMPTY}</span>
      {instance.deployedAt && (
        <div className="text-muted" title={formatDateTime(instance.deployedAt)}>deployed {ago(instance.deployedAt)}</div>
      )}
    </td>
  </tr>
);

const EnvironmentCard = ({ env, region }) => {
  const healthy = env.instances.filter((i) => i.healthStatus === 'Ok').length;
  return (
    <Card className="shadow-sm mb-4" id={`env-${env.name}`} style={{ scrollMarginTop: 80 }}>
      <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h5 className="mb-0">{env.name}</h5>
          <HealthBadge status={env.healthStatus} />
          {env.status && env.status !== 'Ready' && <Badge bg="secondary">{env.status}</Badge>}
        </div>
        <div className="small text-muted d-flex flex-wrap align-items-center column-gap-3">
          <span>{env.application}{env.tier === 'Worker' ? ' (worker)' : ''}</span>
          {!env.error && (
            <span className={healthy < env.instances.length ? 'text-danger fw-semibold' : undefined}>
              {healthy} of {plural(env.instances.length, 'instance')} OK
            </span>
          )}
          {env.id && (
            <a href={consoleUrl(region, env.id)} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center gap-1">
              AWS console <ExternalLink size={12} aria-hidden="true" />
            </a>
          )}
        </div>
      </Card.Header>
      <Card.Body className="py-3 small">
        {env.error ? (
          <p className="text-danger mb-0">{env.error}</p>
        ) : (
          <>
            <RequestSummary env={env} />
            {env.causes.length > 0 && (
              <ul className="mb-0 mt-2 ps-3">
                {env.causes.map((cause) => <li key={cause}>{cause}</li>)}
              </ul>
            )}
          </>
        )}
      </Card.Body>
      {!env.error && (
        <Card.Body className="p-0 border-top">
          <StackedTable responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Instance</th>
                <th>Health</th>
                <th>Cause</th>
                <th className="text-end text-nowrap">Requests (10 s)</th>
                <th className="text-end">5xx</th>
                <th className="text-end">p99</th>
                <th className="text-end">CPU</th>
                <th className="text-end d-none d-lg-table-cell">Load</th>
                <th>Launched</th>
                <th className="d-none d-xl-table-cell">Version</th>
              </tr>
            </thead>
            <tbody>
              {env.instances.length === 0 ? (
                <tr><td colSpan={10} className="p-4 text-center text-muted">No instances</td></tr>
              ) : (
                env.instances.map((instance) => <InstanceRow key={instance.id} instance={instance} />)
              )}
            </tbody>
          </StackedTable>
        </Card.Body>
      )}
    </Card>
  );
};

// What an event row leads with: the status a health change moved to, or what kind of event it is
const EventBadge = ({ event }) => {
  if (event.kind === 'HEALTH') {
    const to = /transitioned from \w+ to (\w+)/.exec(event.message)?.[1];
    return <HealthBadge status={to} />;
  }
  if (event.kind === 'DEPLOY') return <Badge bg="primary">Deploy</Badge>;
  if (event.kind === 'RESTART') return <Badge bg="primary">Restart</Badge>;
  if (event.kind === 'INSTANCE') return <Badge bg="secondary">Instance</Badge>;
  return <Badge bg={event.severity === 'WARN' ? 'warning' : 'danger'}>{event.severity === 'WARN' ? 'Warning' : 'Error'}</Badge>;
};

const EventsCard = ({ data, eventHours, onEventHoursChange, isMobile }) => {
  const [environment, setEnvironment] = useState('ALL');
  const eventWindow = EVENT_WINDOWS.find((w) => w.value === eventHours) || EVENT_WINDOWS[0];
  const events = environment === 'ALL' ? data.events : data.events.filter((e) => e.environment === environment);

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">Events</h5>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select
            size={isMobile ? undefined : 'sm'}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            aria-label="Environment"
            className="w-auto"
          >
            <option value="ALL">All environments</option>
            {data.environments.map((env) => <option key={env.name} value={env.name}>{env.name}</option>)}
          </Form.Select>
          <SegmentedControl
            options={EVENT_WINDOWS.map((w) => ({ value: w.value, label: w.label }))}
            value={eventHours}
            onChange={onEventHoursChange}
            size={isMobile ? undefined : 'sm'}
            ariaLabel="Event window"
          />
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {data.eventsError ? (
          <ErrorState message={data.eventsError} inCard />
        ) : events.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No health changes, deployments or warnings in the last {eventWindow.period}
          </div>
        ) : (
          <StackedTable responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-nowrap">Time</th>
                <th>Environment</th>
                <th>Event</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={`${event.time}-${event.environment}-${event.message}`}>
                  <td className="align-middle text-nowrap">{formatDateTime(event.time)}</td>
                  <td className="align-middle text-nowrap">{event.environment}</td>
                  <td className="align-middle stack-block">
                    <EventBadge event={event} /> <span className="small">{event.message}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </StackedTable>
        )}
      </Card.Body>
    </Card>
  );
};

const ServerHealth = () => {
  const ensureToken = useAuthToken();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [eventHours, setEventHours] = useState(EVENT_WINDOWS[0].value);
  // Only the newest request may update the page, so a slow reply for an earlier window is dropped
  const requestId = useRef(0);

  const fetchData = useCallback(async (hours) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      await ensureToken();
      const res = await serverHealthService.getOverview(hours);
      if (id !== requestId.current) return;
      setData(res.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(serverMessage(err, 'Could not load server health.'));
      console.error(err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken]);

  // Load now, then refresh on a timer while the tab is visible, and straight away when it comes back
  useEffect(() => {
    fetchData(eventHours);
    const interval = setInterval(() => {
      if (!document.hidden) fetchData(eventHours);
    }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) fetchData(eventHours);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData, eventHours]);

  const attention = useMemo(() => (data ? data.environments.filter(needsAttention) : []), [data]);

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Server health</h1>
          <p className="text-muted mb-0">
            Elastic Beanstalk health for every environment, refreshed every 30 seconds.
            {lastUpdated && ` Updated ${formatTime(lastUpdated)}.`}
          </p>
        </Col>
      </Row>

      <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-2 mb-4">
        <Button
          variant="outline-secondary"
          size={isMobile ? undefined : 'sm'}
          className="d-inline-flex align-items-center"
          onClick={() => fetchData(eventHours)}
          disabled={loading}
        >
          <RefreshCw size={14} className="me-1" aria-hidden="true" />Refresh
        </Button>
        {data && data.environments.length > 0 && (
          attention.length === 0 ? (
            <span className="d-inline-flex align-items-center gap-2">
              <CircleCheck size={16} className="text-success" aria-hidden="true" />
              All {plural(data.environments.length, 'environment')} are healthy.
            </span>
          ) : (
            <span className="d-inline-flex flex-wrap align-items-center column-gap-2" role="status">
              <CircleAlert size={16} className="text-danger" aria-hidden="true" />
              <span>
                {attention.length} of {plural(data.environments.length, 'environment')} need attention:
              </span>
              {attention.map((env, index) => (
                <span key={env.name}>
                  <Button variant="link" className="p-0 align-baseline" onClick={() => scrollToEnvironment(env.name)}>
                    {env.name}
                  </Button>
                  {index < attention.length - 1 ? ',' : ''}
                </span>
              ))}
            </span>
          )
        )}
      </div>

      {/* Once there is data, a failed refresh keeps it on screen and says so here */}
      {error && data && <ErrorState message={error} onRetry={() => fetchData(eventHours)} className="mb-4" />}

      {!data && (error
        ? <ErrorState message={error} onRetry={() => fetchData(eventHours)} />
        : <LoadingState label="Loading server health" />)}

      {data && data.environments.length === 0 && (
        <p className="text-muted">No running Elastic Beanstalk environments in {data.region}.</p>
      )}

      {data && data.environments.map((env) => <EnvironmentCard key={env.name} env={env} region={data.region} />)}

      {data && (
        <EventsCard data={data} eventHours={eventHours} onEventHoursChange={setEventHours} isMobile={isMobile} />
      )}
    </Container>
  );
};

export default ServerHealth;
